import { useState, useEffect, useCallback } from 'react';
import { admin as adminApi } from '../api/admin';
import DateTimeStack from './DateTimeStack';
import { RefreshCw, Clock, Trash2, Server, Hash, Building2, Calendar, Eye } from 'lucide-react';
import GridPagination from './GridPagination';
import PageHeader from './layout/PageHeader';
import { MobileFilterBar, type MobileFiltersConfig } from './grid/MobileFilters';
import { usePhone } from './grid/FilterPanel';
import { usePaginationConfig } from '../hooks/usePaginationConfig';
import { RecordLink } from '../preferences/UserPreferences';
import { useOpenRecord } from '../preferences/UserPreferences';

interface LogEntry {
  _lineNumber: number;
  _raw?: string;
  timestamp?: string;
  method?: string;
  url?: string;
  endpoint?: string;
  responseStatus?: number;
  durationMs?: number;
  carrier?: string;
  requestType?: string;
  level?: string;
  context?: string;
  message?: string;
  [key: string]: unknown;
}

interface LogResponse {
  totalLines: number;
  page: number;
  limit: number;
  totalPages: number;
  data: LogEntry[];
}

interface CompanyOption {
  id: string;
  name: string;
}

const LogViewer = () => {
  const openRecord = useOpenRecord();
  const { limit: defaultLimit, ready } = usePaginationConfig();
  const phone = usePhone();

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [logFiles, setLogFiles] = useState<string[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  const [selectedCompany, setSelectedCompany] = useState<string>(
    () => localStorage.getItem('lastSelectedLogCompany') || 'default'
  );
  const [selectedDate, setSelectedDate] = useState<string>(
    () => localStorage.getItem('lastSelectedLogDate') || new Date().toISOString().split('T')[0]
  );
  const [selectedLogFile, setSelectedLogFile] = useState<string>(
    () => localStorage.getItem('lastSelectedLogFile') || 'api-requests'
  );

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1
  });

  const [filters, setFilters] = useState<{
    method: string;
    status: string;
    carrier: string;
    endpoint: string;
    ip: string;
    requestSearch: string;
    responseSearch: string;
    headerSearch: string;
  }>(() => {
    const saved = localStorage.getItem('lastLogFilters');
    if (saved) {
      try {
        return {
          requestSearch: '',
          responseSearch: '',
          headerSearch: '',
          ...JSON.parse(saved)
        };
      } catch (e) {}
    }
    return {
      method: 'ALL',
      status: 'ALL',
      carrier: 'ALL',
      endpoint: '',
      ip: '',
      requestSearch: '',
      responseSearch: '',
      headerSearch: ''
    };
  });

  useEffect(() => {
    localStorage.setItem('lastLogFilters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('lastSelectedLogCompany', selectedCompany);
  }, [selectedCompany]);

  useEffect(() => {
    localStorage.setItem('lastSelectedLogDate', selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (selectedLogFile) {
      localStorage.setItem('lastSelectedLogFile', selectedLogFile);
    }
  }, [selectedLogFile]);

  // Extract unique carriers from current logs to populate filter if it's thirdparty
  const uniqueCarriers = Array.from(new Set(logs.map(l => l.carrier).filter(Boolean)));
  const isAppLog = selectedLogFile.startsWith('application');

  // Fetch log structure (companies list)
  useEffect(() => {
    if (ready) {
      setPagination(p => ({ ...p, limit: defaultLimit }));
      adminApi.getLogStructure()
        .then(res => {
          if (res?.companies) {
            setCompanies(res.companies);
          }
        })
        .catch(console.error);
    }
  }, [ready, defaultLimit]);

  // Fetch available files for selected company and date
  useEffect(() => {
    if (ready) {
      adminApi.getLogFiles(selectedCompany, selectedDate)
        .then(res => {
          const files = res || ['api-requests', 'thirdparty', 'application'];
          setLogFiles(files);
          if (files.length > 0 && (!selectedLogFile || !files.includes(selectedLogFile))) {
            setSelectedLogFile(files[0]);
          }
        })
        .catch(console.error);
    }
  }, [ready, selectedCompany, selectedDate, selectedLogFile]);

  const fetchLogs = useCallback(async () => {
    if (!selectedLogFile) {
      setLogs([]);
      setPagination(prev => ({ ...prev, total: 0, totalPages: 1 }));
      return;
    }
    setLoading(true);
    try {
      const res: LogResponse = await adminApi.getSystemLogs(
        selectedLogFile,
        pagination.page,
        pagination.limit,
        filters,
        selectedCompany,
        selectedDate
      );
      setLogs(res.data || []);
      setPagination(prev => ({
        ...prev,
        total: res.totalLines || 0,
        totalPages: res.totalPages || Math.max(1, Math.ceil((res.totalLines || 0) / prev.limit))
      }));
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [selectedLogFile, pagination.page, pagination.limit, filters, selectedCompany, selectedDate]);

  useEffect(() => {
    if (ready) {
      fetchLogs();
    }
  }, [fetchLogs, ready]);

  const clearLogs = async () => {
    if (!selectedLogFile) return;
    const companyLabel = selectedCompany === 'default' ? 'default' : `company (${selectedCompany})`;
    if (!window.confirm(`Are you sure you want to clear ${selectedLogFile} logs for ${companyLabel} on ${selectedDate}? This action cannot be undone.`)) {
      return;
    }
    setLoading(true);
    try {
      await adminApi.clearSystemLogs(selectedLogFile, selectedCompany, selectedDate);
      await fetchLogs();
    } catch (err) {
      console.error('Failed to clear logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status?: number) => {
    if (!status) return 'bg-slate-100 text-slate-600';
    if (status >= 500) return 'text-red-600 bg-red-50 border-red-100';
    if (status >= 400) return 'text-orange-600 bg-orange-50 border-orange-100';
    if (status >= 300) return 'text-amber-600 bg-amber-50 border-amber-100';
    if (status >= 200) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    return 'bg-slate-100 text-slate-600';
  };

  const getEndpoint = (url?: string, endpoint?: string) => {
    if (endpoint) return endpoint;
    if (!url) return '—';
    try {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const parsed = new URL(url);
        return parsed.pathname || url;
      }
      return url.split('?')[0] || url;
    } catch {
      return url;
    }
  };

  const getLogDetailUrl = (lineNumber: number) => {
    return `/admin/system-logs/entry?filename=${encodeURIComponent(selectedLogFile)}&line=${lineNumber}&company=${encodeURIComponent(selectedCompany)}&date=${encodeURIComponent(selectedDate)}`;
  };

  const openLogDetail = (lineNumber: number) => {
    if (!selectedLogFile) return;

    // Don't open if user is selecting text
    if (window.getSelection()?.toString()) {
      return;
    }

    openRecord(getLogDetailUrl(lineNumber));
  };

  /**
   * Phone: the secondary filters in the filter panel, with the end point /
   * message box as the search beside the icon. Company, date and file stay
   * as they are - they pick which log is shown. Every change applies at once.
   */
  const setFilter = (key: keyof typeof filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  const textField = (id: 'ip' | 'requestSearch' | 'responseSearch' | 'headerSearch', label: string, placeholder: string) => ({
    id,
    label,
    kind: 'text' as const,
    value: filters[id],
    placeholder,
    onChange: (v: string) => setFilter(id, v),
  });
  const mobileFilters: MobileFiltersConfig = {
    fields: [
      ...(!isAppLog
        ? [
            {
              id: 'method',
              label: 'Method',
              kind: 'select' as const,
              value: filters.method,
              defaultValue: 'ALL',
              options: [
                { value: 'ALL', label: 'All Methods' },
                ...['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => ({ value: m, label: m })),
              ],
              onChange: (v: string) => setFilter('method', v),
            },
            {
              id: 'status',
              label: 'Status',
              kind: 'select' as const,
              value: filters.status,
              defaultValue: 'ALL',
              options: [
                { value: 'ALL', label: 'All Statuses' },
                { value: 'SUCCESS', label: 'Success (2xx, 3xx)' },
                { value: 'ERROR', label: 'Error (4xx, 5xx)' },
              ],
              onChange: (v: string) => setFilter('status', v),
            },
          ]
        : []),
      ...(selectedLogFile === 'thirdparty'
        ? [
            {
              id: 'carrier',
              label: 'Carrier',
              kind: 'select' as const,
              value: filters.carrier,
              defaultValue: 'ALL',
              options: [
                { value: 'ALL', label: 'All Carriers' },
                ...uniqueCarriers.map((c: any) => ({ value: String(c), label: String(c) })),
              ],
              onChange: (v: string) => setFilter('carrier', v),
            },
          ]
        : []),
      ...(!isAppLog
        ? [
            textField('ip', 'IP address', 'IP Address...'),
            textField('requestSearch', 'Request contains', 'Request Contains...'),
            textField('responseSearch', 'Response contains', 'Response Contains...'),
            textField('headerSearch', 'Header contains', 'Header Contains...'),
          ]
        : []),
    ],
    // Like "Clear Filters", but the search box keeps what was typed.
    onClear: () => {
      setFilters(prev => ({ method: 'ALL', status: 'ALL', carrier: 'ALL', endpoint: prev.endpoint, ip: '', requestSearch: '', responseSearch: '', headerSearch: '' }));
      setPagination(prev => ({ ...prev, page: 1 }));
    },
  };

  return (
    <div className="w-full space-y-8 pb-12">
      <PageHeader
        title="System Logs"
        subtitle="View detailed system and carrier API logs categorized by company and date."
      />

      {/* Primary Categorization Filters */}
      <div className="bg-white border text-sm font-medium border-slate-200 rounded-2xl p-4 flex flex-col gap-4 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-4 border-b border-slate-100 pb-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Company Filter Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Building2 size={15} className="text-indigo-500" />
                Company:
              </span>
              <select
                title="Filter by Company"
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none font-semibold text-slate-700 min-w-[220px]"
                value={selectedCompany}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedCompany(val);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="default">Default / System (No Company)</option>
                {companies
                  .filter(c => c.id !== 'default')
                  .map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <Calendar size={15} className="text-indigo-500" />
                Date:
              </span>
              <input
                type="date"
                title="Filter by Date"
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none font-semibold text-slate-700"
                value={selectedDate}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedDate(val);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            </div>

            {/* Log File Selector */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                File:
              </span>
              <select
                title="Select Log File"
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none font-semibold text-slate-700 min-w-[160px]"
                value={selectedLogFile}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedLogFile(val);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                {logFiles.map(file => (
                  <option key={file} value={file}>{file}.log</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 text-indigo-700 font-bold transition-all shadow-sm disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={clearLogs}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 text-red-700 font-bold transition-all shadow-sm disabled:opacity-50"
            >
              <Trash2 size={16} />
              Clear Logs
            </button>
          </div>
        </div>

        {/* Secondary Content & Header Filters */}
        {phone ? (
          <MobileFilterBar
            config={mobileFilters}
            search={
              <input
                type="text"
                placeholder={isAppLog ? "Search Context/Message..." : "End Point..."}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none"
                value={filters.endpoint}
                onChange={(e) => setFilter('endpoint', e.target.value)}
              />
            }
          />
        ) : (
        <div className="flex items-center gap-3 flex-wrap">
          {!isAppLog && (
            <select
              title="Filter by Method"
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none min-w-[130px]"
              value={filters.method}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, method: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="ALL">All Methods</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
              <option value="PATCH">PATCH</option>
            </select>
          )}

          {!isAppLog && (
            <select
              title="Filter by Status"
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none min-w-[140px]"
              value={filters.status}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, status: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="ALL">All Statuses</option>
              <option value="SUCCESS">Success (2xx, 3xx)</option>
              <option value="ERROR">Error (4xx, 5xx)</option>
            </select>
          )}

          {selectedLogFile === 'thirdparty' && (
            <select
              title="Filter by Carrier"
              className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none min-w-[130px]"
              value={filters.carrier}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, carrier: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="ALL">All Carriers</option>
              {uniqueCarriers.map((c: any) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}

          <input
            type="text"
            placeholder={isAppLog ? "Search Context/Message..." : "End Point..."}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none min-w-[180px]"
            value={filters.endpoint}
            onChange={(e) => {
              setFilters(prev => ({ ...prev, endpoint: e.target.value }));
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          />

          {!isAppLog && (
            <>
              <input
                type="text"
                placeholder="IP Address..."
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none min-w-[140px]"
                value={filters.ip}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, ip: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
              <input
                type="text"
                placeholder="Request Contains..."
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none min-w-[150px]"
                value={filters.requestSearch}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, requestSearch: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
              <input
                type="text"
                placeholder="Response Contains..."
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none min-w-[160px]"
                value={filters.responseSearch}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, responseSearch: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
              <input
                type="text"
                placeholder="Header Contains..."
                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none min-w-[150px]"
                value={filters.headerSearch}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, headerSearch: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            </>
          )}

          <button
            onClick={() => {
              setFilters({ method: 'ALL', status: 'ALL', carrier: 'ALL', endpoint: '', ip: '', requestSearch: '', responseSearch: '', headerSearch: '' });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="px-4 py-2 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-xl transition-colors shadow-sm font-semibold"
          >
            Clear Filters
          </button>
        </div>
        )}
      </div>

      {/* Grid Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm flex flex-col">
        <div className="overflow-x-auto min-h-[400px]">
          <table className="monitoring-grid bg-white w-full">
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap w-16">
                  <div className="flex items-center gap-1"><Hash size={12}/> Line</div>
                </th>
                <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap w-44">
                  <div className="flex items-center gap-1"><Clock size={12}/> Timestamp</div>
                </th>
                {isAppLog ? (
                  <>
                    <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap w-24">Level</th>
                    <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap w-44">Context</th>
                    <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">Message</th>
                  </>
                ) : (
                  <>
                    <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap w-24">Method</th>
                    <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">End Point</th>
                    <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap w-24">Status</th>
                    <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap w-24">Latency</th>
                    {selectedLogFile === 'thirdparty' && (
                      <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap w-32">Carrier</th>
                    )}
                  </>
                )}
                <th className="font-black text-slate-500 uppercase tracking-widest whitespace-nowrap w-20 text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && logs.length === 0 ? (
                <tr>
                  <td colSpan={isAppLog ? 6 : (selectedLogFile === 'thirdparty' ? 8 : 7)} className="text-center text-slate-400 font-medium py-12">
                    <RefreshCw size={24} className="animate-spin mx-auto mb-3 text-indigo-500" />
                    Loading file logs…
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={isAppLog ? 6 : (selectedLogFile === 'thirdparty' ? 8 : 7)} className="text-center text-slate-400 font-medium py-12">
                    <div className="sticky left-0 whitespace-normal w-[calc(100vw-4rem)] sm:w-[calc(100vw-6rem)] lg:static lg:w-auto">No log entries found for the selected company, date, and filters.</div>
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => (
                  <tr
                    key={idx}
                    onClick={() => openLogDetail(log._lineNumber)}
                    className="hover:bg-slate-50/80 cursor-pointer transition-colors group"
                  >
                    <td className="text-slate-400 font-mono whitespace-nowrap">
                      #{log._lineNumber}
                    </td>
                    <td className="font-medium text-slate-500 whitespace-nowrap">
                      {log.timestamp && isNaN(Date.parse(log.timestamp)) ? log.timestamp : <DateTimeStack value={log.timestamp} icon={false} />}
                    </td>
                    {isAppLog ? (
                      <>
                        <td className="whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border
                            ${log.level === 'ERROR' ? 'bg-red-50 text-red-600 border-red-200' : 
                              log.level === 'WARN' ? 'bg-orange-50 text-orange-600 border-orange-200' :
                              'bg-blue-50 text-blue-600 border-blue-200'}
                          `}>
                            {log.level || 'UKN'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap">
                          <span className="text-xs font-bold text-slate-600">{log.context || '—'}</span>
                        </td>
                        <td>
                          <div className="text-xs font-mono text-slate-700 max-w-2xl break-words" title={log.message as string}>
                            {log.message || log._raw || '—'}
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border
                            ${log.method === 'POST' ? 'bg-purple-50 text-purple-600 border-purple-200' : 
                              log.method === 'GET' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                              'bg-slate-50 text-slate-600 border-slate-200'}
                          `}>
                            {log.method || 'UKN'}
                          </span>
                        </td>
                        <td>
                          {log.url && (log.url.startsWith('http://') || log.url.startsWith('https://')) ? (
                            <a
                              href={log.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs font-mono text-indigo-600 hover:text-indigo-800 hover:underline inline-block truncate max-w-sm"
                              title={log.url}
                            >
                              {getEndpoint(log.url, log.endpoint)}
                            </a>
                          ) : (
                            <div className="text-xs font-mono text-slate-700 truncate max-w-sm" title={log.url || log.endpoint}>
                              {getEndpoint(log.url, log.endpoint)}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${getStatusColor(log.responseStatus)}`}>
                            {log.responseStatus || '—'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600">
                            {log.durationMs != null ? `${log.durationMs}ms` : '—'}
                          </div>
                        </td>
                        {selectedLogFile === 'thirdparty' && (
                          <td className="whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Server size={14} className="text-slate-400" />
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
                                {log.carrier || '—'}
                              </span>
                            </div>
                          </td>
                        )}
                      </>
                    )}
                    <td className="whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                      <RecordLink
                        to={getLogDetailUrl(log._lineNumber)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 hover:text-indigo-800 rounded-lg border border-indigo-100 transition-colors shadow-sm"
                        title="View log details (opens in a new tab)"
                      >
                        <Eye size={13} />
                        View
                      </RecordLink>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <GridPagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          label="File log entries"
          loading={loading}
          limit={pagination.limit}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onLimitChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
        />
      </div>
    </div>
  );
};

export default LogViewer;
